// /opt/isp_erp/frontend/src/components/tickets/TicketDetail.jsx

import { useAuth } from '../../auth/useAuth';
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Grid,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Avatar,
    Tabs,
    Tab,
    Paper,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Comment as CommentIcon,
    CheckCircle as CheckCircleIcon,
    MedicalServices as MedicalIcon,
    Engineering as EngineeringIcon,
    Schedule as ScheduleIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    SwapHoriz as SwapHorizIcon,
    Add as AddIcon,
    Delete as DeleteIconMaterial,
    PhotoCamera as PhotoIcon,
    Image as ImageIcon,
    Inventory as InventoryIcon,
    Replay as ReplayIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ticketsAPI } from '../../api/tickets.api';
import { scheduleAPI } from '../../api/schedule.api';
import api from '../../api/axios';
import TicketTimeline from './TicketTimeline';
import CloseInstalacionDialog from './CloseInstalacionDialog';
import CloseRetiroDialog from './CloseRetiroDialog';
import CloseCambioEquipoDialog from './CloseCambioEquipoDialog';
import DateTimeSelector from '../DateTimeSelector';
import PDFButton from './PDFButton';
import FotoUploader from './FotoUploader';
import ClienteBitacora from './ClienteBitacora';

const statusColors = {
    abierto: 'default',
    en_diagnostico: 'primary',
    asignado_campo: 'warning',
    resuelto: 'success',
    cerrado: 'secondary',
    cancelado: 'error'
};

const priorityColors = {
    baja: 'success',
    media: 'info',
    alta: 'warning',
    critica: 'error'
};

export default function TicketDetail({ open, onClose, ticket: initialTicket, onUpdate }) {
    const [activeTab, setActiveTab] = useState(0);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [isLoadingTicket, setIsLoadingTicket] = useState(false);
    const { user } = useAuth();
    const [ticket, setTicket] = useState(initialTicket);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [closeRetiroDialogOpen, setCloseRetiroDialogOpen] = useState(false);
    const [closeCambioDialogOpen, setCloseCambioDialogOpen] = useState(false);
    const [diagnosticData, setDiagnosticData] = useState({
        ont_status: null,
        rx_power: '',
        tests_performed: '',
        diagnostic_notes: ''
    });
    const [fieldData, setFieldData] = useState({
        field_actions: '',
        final_rx_power: '',
        materials_used: '',
        evidence_photo_url: ''
    });
    const [technicians, setTechnicians] = useState([]);
    const [selectedTechnician, setSelectedTechnician] = useState('');
    const [visitDate, setVisitDate] = useState('');

    // Estados para materiales en incidentes
    const [materialesUtilizados, setMaterialesUtilizados] = useState([]);
    const [catalogoMateriales, setCatalogoMateriales] = useState([]);
    const [nuevoMaterial, setNuevoMaterial] = useState({ material_id: '', cantidad: 1, observaciones: '' });

    // ✅ Estado para fotos
    const [fotosBase64, setFotosBase64] = useState([]);

    // ✅ Estado para bitácora del cliente
    const [bitacoraClienteOpen, setBitacoraClienteOpen] = useState(false);

    // Estados para verificación de disponibilidad
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [availabilityError, setAvailabilityError] = useState('');
    const [isAvailable, setIsAvailable] = useState(null);

    // Función para cargar el ticket más reciente desde el servidor
    const loadLatestTicket = async () => {
        if (!initialTicket?.id) return null;

        setIsLoadingTicket(true);
        try {
            console.log('🔄 Cargando ticket más reciente ID:', initialTicket.id);
            const updatedTicket = await ticketsAPI.getById(initialTicket.id);
            console.log('✅ Ticket cargado:', updatedTicket);
            setTicket(updatedTicket);
            return updatedTicket;
        } catch (error) {
            console.error('❌ Error loading ticket:', error);
            return null;
        } finally {
            setIsLoadingTicket(false);
        }
    };

    // Recargar el ticket actual desde el servidor (después de acciones)
    const refreshTicket = async () => {
        if (ticket && ticket.id) {
            try {
                console.log('🔄 Refrescando ticket ID:', ticket.id);
                const updatedTicket = await ticketsAPI.getById(ticket.id);
                console.log('✅ Ticket actualizado:', updatedTicket);
                console.log('📝 Comentarios:', updatedTicket.comments);
                setTicket(updatedTicket);
                if (onUpdate) {
                    onUpdate();
                }
                return updatedTicket;
            } catch (error) {
                console.error('❌ Error refreshing ticket:', error);
            }
        }
        return null;
    };

    // Cargar catálogo de materiales
    const cargarMateriales = async () => {
        try {
            const response = await api.get('/warehouse/materiales');
            setCatalogoMateriales(response.data || []);
        } catch (error) {
            console.error('Error cargando materiales:', error);
        }
    };

    // Agregar material a la lista
    const agregarMaterial = () => {
        if (!nuevoMaterial.material_id) {
            alert('Seleccione un material');
            return;
        }
        if (!nuevoMaterial.cantidad || nuevoMaterial.cantidad < 1) {
            alert('Ingrese una cantidad válida');
            return;
        }
        const materialInfo = catalogoMateriales.find(m => m.id === nuevoMaterial.material_id);
        setMaterialesUtilizados([...materialesUtilizados, {
            material_id: nuevoMaterial.material_id,
            nombre: materialInfo?.nombre,
            cantidad: nuevoMaterial.cantidad,
            observaciones: nuevoMaterial.observaciones
        }]);
        setNuevoMaterial({ material_id: '', cantidad: 1, observaciones: '' });
    };

    // Eliminar material de la lista
    const eliminarMaterial = (index) => {
        setMaterialesUtilizados(materialesUtilizados.filter((_, i) => i !== index));
    };

    // Función para abrir el diálogo de cierre de instalación
    const handleCloseInstallation = () => {
        setCloseDialogOpen(true);
    };

    // Función para abrir el diálogo de cierre de retiro
    const handleCloseRetiro = () => {
        setCloseRetiroDialogOpen(true);
    };

    // Función para abrir el diálogo de cierre de cambio de equipo
    const handleCloseCambio = () => {
        setCloseCambioDialogOpen(true);
    };

    // Función para manejar el éxito del cierre
    const handleCloseSuccess = () => {
        refreshTicket();
    };

    // Cargar datos cuando se abre el modal
    useEffect(() => {
        if (open && initialTicket?.id) {
            loadLatestTicket();
            cargarMateriales();
        }
    }, [open, initialTicket?.id]);

    // Actualizar datos de diagnóstico y TRABAJO DE CAMPO cuando cambia el ticket
    useEffect(() => {
        if (ticket) {
            // Diagnóstico
            if (ticket.tests_performed && Array.isArray(ticket.tests_performed)) {
                setDiagnosticData({
                    ...diagnosticData,
                    tests_performed: ticket.tests_performed.join(', ')
                });
            } else {
                setDiagnosticData(prev => ({ ...prev, tests_performed: '' }));
            }
            if (ticket.diagnostic_notes) {
                setDiagnosticData(prev => ({ ...prev, diagnostic_notes: ticket.diagnostic_notes }));
            }
            if (ticket.ont_status !== undefined) {
                setDiagnosticData(prev => ({ ...prev, ont_status: ticket.ont_status }));
            }
            if (ticket.rx_power) {
                setDiagnosticData(prev => ({ ...prev, rx_power: ticket.rx_power }));
            }

            // Cargar datos de TRABAJO DE CAMPO guardados
            if (ticket.field_actions) {
                setFieldData(prev => ({ ...prev, field_actions: ticket.field_actions }));
            }
            if (ticket.final_rx_power) {
                setFieldData(prev => ({ ...prev, final_rx_power: ticket.final_rx_power }));
            }

            // Manejar materials_used correctamente (evitar error de lista vacía)
            if (ticket.materials_used) {
                if (Array.isArray(ticket.materials_used) && ticket.materials_used.length === 0) {
                    setFieldData(prev => ({ ...prev, materials_used: '' }));
                } else if (typeof ticket.materials_used === 'object') {
                    if (Object.keys(ticket.materials_used).length === 0) {
                        setFieldData(prev => ({ ...prev, materials_used: '' }));
                    } else {
                        setFieldData(prev => ({ ...prev, materials_used: JSON.stringify(ticket.materials_used, null, 2) }));
                    }
                } else {
                    setFieldData(prev => ({ ...prev, materials_used: ticket.materials_used }));
                }
            } else {
                setFieldData(prev => ({ ...prev, materials_used: '' }));
            }

            if (ticket.evidence_photo_url) {
                setFieldData(prev => ({ ...prev, evidence_photo_url: ticket.evidence_photo_url }));
            }

            // Cargar materiales utilizados si vienen en el ticket
            if (ticket.materials_used && typeof ticket.materials_used === 'object' && ticket.materials_used.materiales_lista) {
                setMaterialesUtilizados(ticket.materials_used.materiales_lista);
            }
        }
    }, [ticket]);

    // Cargar técnicos cuando se abre el modal
    useEffect(() => {
        if (open) {
            loadTechnicians();
        }
    }, [open]);

    const loadTechnicians = async () => {
        try {
            const data = await ticketsAPI.getTechnicians();
            setTechnicians(data || []);
        } catch (error) {
            console.error('Error loading technicians:', error);
            setTechnicians([]);
        }
    };

    // Mostrar loading mientras se carga el ticket
    if (isLoadingTicket) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}> 
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Cargando ticket...</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    if (!ticket) return null;

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        setLoading(true);
        console.log('💬 Agregando comentario:', newComment);
        try {
            const result = await ticketsAPI.addComment(ticket.id, {
                comment: newComment,
                is_internal: false
            });
            console.log('✅ Comentario guardado:', result);
            setNewComment('');
            await refreshTicket();
        } catch (error) {
            console.error('❌ Error adding comment:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateDiagnostic = async () => {
        setLoading(true);
        console.log('🔧 Actualizando diagnóstico:', diagnosticData);
        try {
            const result = await ticketsAPI.updateDiagnostic(ticket.id, {
                ont_status: diagnosticData.ont_status,
                rx_power: diagnosticData.rx_power ? parseFloat(diagnosticData.rx_power) : null,
                tests_performed: diagnosticData.tests_performed.split(',').map(t => t.trim()).filter(t => t),
                diagnostic_notes: diagnosticData.diagnostic_notes
            });
            console.log('✅ Diagnóstico actualizado:', result);
            await refreshTicket();
        } catch (error) {
            console.error('❌ Error updating diagnostic:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolveRemotely = async () => {
        if (!window.confirm('¿El problema fue resuelto remotamente? Se marcará el ticket como resuelto.')) return;
        setLoading(true);
        console.log('✅ Resolviendo ticket remotamente');
        try {
            const result = await ticketsAPI.resolve(ticket.id, {
                resolution_notes: diagnosticData.diagnostic_notes || 'Resuelto remotamente',
                speed_test_result: {
                    download: null,
                    upload: null,
                    ping: null
                }
            });
            console.log('✅ Ticket resuelto:', result);
            await refreshTicket();
            onClose();
        } catch (error) {
            console.error('❌ Error resolving ticket:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleScheduleVisit = async () => {
        if (!selectedTechnician || !visitDate) {
            alert('Seleccione un técnico y una fecha de visita');
            return;
        }

        setCheckingAvailability(true);
        setAvailabilityError('');
        setIsAvailable(null);

        try {
            let fechaStr = visitDate;
            if (fechaStr.includes('T')) {
                fechaStr = fechaStr.split('T')[0];
            }

            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(fechaStr)) {
                throw new Error('Formato de fecha inválido');
            }

            console.log('📅 Verificando disponibilidad para fecha:', fechaStr);
            console.log('👤 Técnico seleccionado:', selectedTechnician);

            const response = await api.get(`/tickets/tecnicos-disponibles-por-fecha`, {
                params: { fecha: fechaStr }
            });

            console.log('📋 Técnicos disponibles:', response.data);

            const tecnicosDisponibles = response.data || [];
            const tecnicoDisponible = tecnicosDisponibles.some(t => t.id === parseInt(selectedTechnician));

            if (!tecnicoDisponible) {
                try {
                    const permisoResponse = await api.get(`/tickets/tecnicos/${selectedTechnician}/permisos`);
                    const permisos = permisoResponse.data?.permisos || [];
                    const permisoActivo = permisos.find(p =>
                        p.estado === 'aprobado' &&
                        p.fecha_inicio <= fechaStr &&
                        p.fecha_fin >= fechaStr
                    );

                    if (permisoActivo) {
                        setAvailabilityError(`⚠️ El técnico tiene permiso en esta fecha (${fechaStr}). Motivo: ${permisoActivo.motivo || 'No especificado'}`);
                    } else {
                        setAvailabilityError(`⚠️ El técnico no está disponible en esta fecha (${fechaStr}).`);
                    }
                } catch (permisoErr) {
                    setAvailabilityError(`⚠️ El técnico no está disponible en esta fecha (${fechaStr}).`);
                }
                setIsAvailable(false);
                setCheckingAvailability(false);
                return;
            }

            const result = await scheduleAPI.checkAvailability(
                selectedTechnician,
                visitDate,
                60
            );

            if (!result.available) {
                setAvailabilityError('El técnico ya tiene un trabajo programado en este horario.');
                setIsAvailable(false);
                setCheckingAvailability(false);
                return;
            }

            setIsAvailable(true);

            setLoading(true);
            console.log('📅 Programando visita:', { technician: selectedTechnician, visitDate });
            try {
                await ticketsAPI.scheduleVisit(ticket.id, visitDate, selectedTechnician);
                await refreshTicket();
                setSelectedTechnician('');
                setVisitDate('');
                setIsAvailable(null);
                setAvailabilityError('');
            } catch (error) {
                console.error('Error scheduling visit:', error);
                if (error.response?.data?.detail?.includes('permiso')) {
                    setAvailabilityError('⚠️ El técnico tiene permiso en esta fecha.');
                } else {
                    setAvailabilityError(error.response?.data?.detail || 'Error al programar visita');
                }
                setIsAvailable(false);
            } finally {
                setLoading(false);
            }
        } catch (err) {
            console.error('Error checking availability:', err);
            console.error('Error response:', err.response?.data);
            if (err.response?.status === 422) {
                setAvailabilityError('⚠️ Formato de fecha inválido. Por favor selecciona otra fecha.');
            } else if (err.response?.status === 404) {
                setAvailabilityError('⚠️ El servicio de verificación de disponibilidad no está disponible. Contacta al administrador.');
            } else {
                setAvailabilityError(err.response?.data?.detail || 'Error al verificar disponibilidad del técnico');
            }
            setIsAvailable(false);
        } finally {
            setCheckingAvailability(false);
        }
    };

    const handleUpdateFieldWork = async () => {
        setLoading(true);
        console.log('🔨 Actualizando trabajo de campo:', fieldData);
        console.log('📦 Materiales utilizados:', materialesUtilizados);

        try {
            let materials = {};
            if (fieldData.materials_used && fieldData.materials_used.trim() && fieldData.materials_used !== '[]' && fieldData.materials_used !== '{}') {
                try {
                    const parsed = JSON.parse(fieldData.materials_used);
                    if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                        materials = parsed;
                    }
                } catch (e) {
                    materials = { description: fieldData.materials_used };
                }
            }

            if (materialesUtilizados.length > 0) {
                materials.materiales_lista = materialesUtilizados;
            }

            await ticketsAPI.updateFieldWork(ticket.id, {
                field_actions: fieldData.field_actions,
                final_rx_power: fieldData.final_rx_power ? parseFloat(fieldData.final_rx_power) : null,
                materials_used: materials,
                evidence_photo_url: fieldData.evidence_photo_url
            });
            await refreshTicket();
            alert('✅ Trabajo de campo guardado correctamente');
        } catch (error) {
            console.error('Error updating field work:', error);
            alert('Error al guardar el trabajo de campo: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    // ✅ handleResolve con integración de fotos
    const handleResolve = async () => {
        if (!window.confirm('¿Marcar este ticket como resuelto? Se descontarán los materiales del stock del técnico y se guardarán las fotos en la nube.')) return;
        setLoading(true);
        console.log('✅ Resolviendo ticket desde campo');
        console.log('📦 Materiales a consumir:', materialesUtilizados);
        console.log('📸 Fotos a subir:', fotosBase64.length);

        try {
            const payload = {
                resolution_notes: fieldData.field_actions || 'Ticket resuelto por técnico',
                materiales_utilizados: materialesUtilizados.map(m => ({
                    nombre: m.nombre,
                    cantidad: m.cantidad,
                    observaciones: m.observaciones || ''
                })),
                fotos: fotosBase64
            };

            console.log('Payload a enviar:', payload);

            const result = await ticketsAPI.resolve(ticket.id, payload);
            console.log('✅ Ticket resuelto:', result);
            await refreshTicket();
            alert('✅ Ticket resuelto, fotos guardadas y materiales descontados del stock');
            onClose();
        } catch (error) {
            console.error('Error resolving ticket:', error);
            alert(error.response?.data?.detail || 'Error al resolver el ticket');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseTicket = async () => {
        if (!window.confirm('¿Cerrar este ticket definitivamente?')) return;
        setLoading(true);
        console.log('🔒 Cerrando ticket definitivamente');
        try {
            await ticketsAPI.close(ticket.id);
            await refreshTicket();
            onClose();
        } catch (error) {
            console.error('Error closing ticket:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTicket = async () => {
        if (!window.confirm('¿Eliminar este ticket definitivamente? Esta acción no se puede deshacer.')) return;
        setLoading(true);
        try {
            await ticketsAPI.deleteTicket(ticket.id);
            if (onUpdate) onUpdate();
            onClose();
        } catch (error) {
            console.error('Error deleting ticket:', error);
            alert(error.response?.data?.detail || 'Error al eliminar el ticket');
        } finally {
            setLoading(false);
        }
    };

    // ✅ Función para reabrir ticket
    const handleReopenTicket = async () => {
        const motivo = window.prompt('Motivo de reapertura (opcional):');
        
        setLoading(true);
        try {
            await ticketsAPI.reopenTicket(ticket.id, motivo || '');
            await refreshTicket();
            alert('✅ Ticket reabierto correctamente');
        } catch (error) {
            console.error('Error reopening ticket:', error);
            alert(error.response?.data?.detail || 'Error al reabrir el ticket');
        } finally {
            setLoading(false);
        }
    };

    const canDiagnose = ticket.status === 'abierto' || ticket.status === 'en_diagnostico';
    const canAssign = ticket.status === 'en_diagnostico';
    const canDoFieldWork = ticket.status === 'asignado_campo';
    const canResolve = ticket.status === 'asignado_campo';
    const canClose = ticket.status === 'resuelto';
    const canResolveRemotely = ticket.status === 'abierto' || ticket.status === 'en_diagnostico';
    const canCloseInstallation = ticket.ticket_type === 'instalacion' && ticket.status === 'abierto';
    const canCloseRetiro = ticket.ticket_type === 'retiro' && ticket.status === 'abierto';
    const canCloseCambio = ticket.ticket_type === 'cambio_equipo' && ticket.status === 'abierto';
    
    // ✅ Condición para mostrar botón Reabrir
    const canReopen = (ticket.status === 'resuelto' || ticket.status === 'cerrado') && 
                      (user?.is_superuser || user?.roles?.includes('admin') || user?.roles?.includes('supervisor'));

    const getPDFType = () => {
        if (ticket.ticket_type === 'instalacion') return 'instalacion';
        if (ticket.ticket_type === 'cambio_equipo') return 'cambio_equipo';
        if (ticket.ticket_type === 'retiro') return 'retiro';
        return 'incidente';
    };

    const getPDFExtraData = () => {
        const data = {};

        if (ticket.ticket_type === 'retiro') {
            if (ticket.materials_used && typeof ticket.materials_used === 'object') {
                if (ticket.materials_used.equipos_retirados) {
                    data.equipos = ticket.materials_used.equipos_retirados;
                } else if (ticket.materials_used.equipos) {
                    data.equipos = ticket.materials_used.equipos;
                }
                if (ticket.materials_used.materiales_consumidos) {
                    data.materiales = ticket.materials_used.materiales_consumidos;
                } else if (ticket.materials_used.materiales_lista) {
                    data.materiales = ticket.materials_used.materiales_lista;
                } else if (ticket.materials_used.materiales) {
                    data.materiales = ticket.materials_used.materiales;
                }
            }
        }

        if (ticket.ticket_type === 'cambio_equipo') {
            if (ticket.materials_used && typeof ticket.materials_used === 'object') {
                if (ticket.materials_used.equipos_viejos) {
                    data.equipos_viejos = ticket.materials_used.equipos_viejos;
                }
                if (ticket.materials_used.equipos_nuevos) {
                    data.equipos_nuevos = ticket.materials_used.equipos_nuevos;
                }
                if (ticket.materials_used.materiales_consumidos) {
                    data.materiales = ticket.materials_used.materiales_consumidos;
                } else if (ticket.materials_used.materiales_lista) {
                    data.materiales = ticket.materials_used.materiales_lista;
                }
            }
        }

        if (ticket.ticket_type === 'instalacion') {
            if (ticket.materials_used && typeof ticket.materials_used === 'object') {
                if (ticket.materials_used.equipos) {
                    data.equipos = ticket.materials_used.equipos;
                }
                if (ticket.materials_used.materiales_consumidos) {
                    data.materiales = ticket.materials_used.materiales_consumidos;
                } else if (ticket.materials_used.materiales_lista) {
                    data.materiales = ticket.materials_used.materiales_lista;
                }
            }
        }

        return data;
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6">
                                Ticket {ticket.ticket_number}
                                <Chip
                                    label={ticket.status}
                                    color={statusColors[ticket.status] || 'default'}
                                    size="small"
                                    sx={{ ml: 2 }}
                                />
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {(ticket.status?.toLowerCase() === 'cerrado' || ticket.status?.toLowerCase() === 'resuelto') && (
                                <PDFButton
                                    ticketId={ticket.id}
                                    ticketNumber={ticket.ticket_number}
                                    type={getPDFType()}
                                    extraData={getPDFExtraData()}
                                    variant="icon"
                                />
                            )}
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {/* Información del cliente */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    Información del Cliente
                                </Typography>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2">{ticket.customer_name || `Cliente ID: ${ticket.customer_id}`}</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2">{ticket.contact_phone}</Typography>
                                    {ticket.contact_phone_alt && (
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                            ({ticket.contact_phone_alt})
                                        </Typography>
                                    )}
                                </Box>
                                {ticket.customer_address && (
                                    <Box display="flex" alignItems="center">
                                        <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                        <Typography variant="body2">{ticket.customer_address}</Typography>
                                    </Box>
                                )}
                                
                                {/* ✅ Botón Bitácora del Cliente */}
                                {ticket.customer_id && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<HistoryIcon />}
                                        onClick={() => setBitacoraClienteOpen(true)}
                                        sx={{ mt: 1 }}
                                    >
                                        Ver Bitácora del Cliente
                                    </Button>
                                )}
                            </Paper>
                        </Grid>

                        {/* SLAs */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    Tiempos SLA
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            Límite de Respuesta
                                        </Typography>
                                        <Typography variant="body2">
                                            {ticket.response_deadline ? format(new Date(ticket.response_deadline), 'dd/MM/yyyy HH:mm', { locale: es }) : 'No definido'}
                                            {ticket.response_deadline && new Date(ticket.response_deadline) < new Date() && !ticket.responded_at && (
                                                <Chip label="VENCIDO" color="error" size="small" sx={{ ml: 1 }} />
                                            )}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            Límite de Resolución
                                        </Typography>
                                        <Typography variant="body2">
                                            {ticket.resolution_deadline ? format(new Date(ticket.resolution_deadline), 'dd/MM/yyyy HH:mm', { locale: es }) : 'No definido'}
                                            {ticket.resolution_deadline && new Date(ticket.resolution_deadline) < new Date() && ticket.status !== 'resuelto' && ticket.status !== 'cerrado' && (
                                                <Chip label="VENCIDO" color="error" size="small" sx={{ ml: 1 }} />
                                            )}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Timeline */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    Línea de Tiempo
                                </Typography>
                                <TicketTimeline ticket={ticket} />
                            </Paper>
                        </Grid>

                        {/* Tabs de acciones */}
                        <Grid item xs={12}>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                                <Tab label="Comentarios" />
                                <Tab label="Diagnóstico" disabled={!canDiagnose && !ticket.diagnostic_notes} />
                                <Tab label="Asignación" disabled={!canAssign} />
                                <Tab label="Trabajo Campo" disabled={!canDoFieldWork && !ticket.field_actions} />
                                <Tab label="Cierre" />
                            </Tabs>

                            <Box sx={{ mt: 2 }}>
                                {activeTab === 0 && (
                                    <Box>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            variant="outlined"
                                            placeholder="Agregar un comentario..."
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            disabled={loading}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddComment}
                                            disabled={!newComment.trim() || loading}
                                            sx={{ mt: 1 }}
                                        >
                                            {loading ? <CircularProgress size={24} /> : 'Agregar Comentario'}
                                        </Button>
                                        <List sx={{ mt: 2 }}>
                                            {ticket.comments?.map((comment, idx) => (
                                                <ListItem key={idx} divider>
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                            <CommentIcon />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={comment.user_name}
                                                        secondary={comment.comment}
                                                        secondaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {comment.created_at ? format(new Date(comment.created_at), 'dd/MM/yyyy HH:mm') : 'Fecha no disponible'}
                                                    </Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                {activeTab === 1 && (
                                    <Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Estado ONT</InputLabel>
                                                    <Select
                                                        value={diagnosticData.ont_status === null ? '' : String(diagnosticData.ont_status)}
                                                        onChange={(e) => setDiagnosticData({ ...diagnosticData, ont_status: e.target.value === 'true' })}
                                                        label="Estado ONT"
                                                    >
                                                        <MenuItem value="true">Online</MenuItem>
                                                        <MenuItem value="false">Offline</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Potencia RX (dBm)"
                                                    type="number"
                                                    value={diagnosticData.rx_power}
                                                    onChange={(e) => setDiagnosticData({ ...diagnosticData, rx_power: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Pruebas realizadas (separadas por coma)"
                                                    value={diagnosticData.tests_performed}
                                                    onChange={(e) => setDiagnosticData({ ...diagnosticData, tests_performed: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    label="Notas de diagnóstico"
                                                    value={diagnosticData.diagnostic_notes}
                                                    onChange={(e) => setDiagnosticData({ ...diagnosticData, diagnostic_notes: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleUpdateDiagnostic}
                                                    disabled={loading}
                                                    startIcon={<MedicalIcon />}
                                                >
                                                    Guardar Diagnóstico
                                                </Button>
                                                {canResolveRemotely && (
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        onClick={handleResolveRemotely}
                                                        disabled={loading}
                                                        startIcon={<CheckCircleIcon />}
                                                        sx={{ ml: 2 }}
                                                    >
                                                        Resolver Ticket (Solución Remota)
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {activeTab === 2 && (
                                    <Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Técnico Asignado</InputLabel>
                                                    <Select
                                                        value={selectedTechnician}
                                                        onChange={(e) => {
                                                            setSelectedTechnician(e.target.value);
                                                            setAvailabilityError('');
                                                            setIsAvailable(null);
                                                            setVisitDate('');
                                                        }}
                                                        label="Técnico Asignado"
                                                    >
                                                        {technicians.map((tech) => (
                                                            <MenuItem key={tech.id} value={tech.id}>
                                                                {tech.full_name} - {tech.phone || 'Sin teléfono'}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <DateTimeSelector
                                                    technicianId={selectedTechnician}
                                                    onDateTimeChange={(date) => {
                                                        if (!date) {
                                                            setVisitDate('');
                                                            setAvailabilityError('');
                                                            setIsAvailable(null);
                                                            return;
                                                        }
                                                        const year = date.getFullYear();
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const hours = String(date.getHours()).padStart(2, '0');
                                                        const minutes = String(date.getMinutes()).padStart(2, '0');
                                                        const localDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
                                                        setVisitDate(localDateTime);
                                                        setAvailabilityError('');
                                                        setIsAvailable(null);
                                                    }}
                                                    durationMinutes={60}
                                                    label="Fecha y Hora de Visita"
                                                />
                                                {availabilityError && (
                                                    <Alert severity="error" sx={{ mt: 1 }}>
                                                        {availabilityError}
                                                    </Alert>
                                                )}
                                                {isAvailable === true && visitDate && (
                                                    <Alert severity="success" sx={{ mt: 1 }}>
                                                        ✅ Técnico disponible en este horario
                                                    </Alert>
                                                )}
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleScheduleVisit}
                                                    disabled={loading || checkingAvailability || !selectedTechnician || !visitDate}
                                                    startIcon={checkingAvailability ? <CircularProgress size={20} /> : <ScheduleIcon />}
                                                >
                                                    {checkingAvailability ? 'Verificando...' : 'Programar Visita'}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {activeTab === 3 && (
                                    <Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    label="Acciones realizadas"
                                                    value={fieldData.field_actions}
                                                    onChange={(e) => setFieldData({ ...fieldData, field_actions: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Potencia final (dBm)"
                                                    type="number"
                                                    value={fieldData.final_rx_power}
                                                    onChange={(e) => setFieldData({ ...fieldData, final_rx_power: e.target.value })}
                                                />
                                            </Grid>

                                            {/* ✅ Sección para subir fotos */}
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PhotoIcon color="primary" />
                                                    Subir Fotos del Incidente
                                                </Typography>
                                                <Alert severity="info" sx={{ mb: 2 }}>
                                                    Las fotos se guardarán en la nube y se incluirán en el PDF de resolución.
                                                    {fotosBase64.length > 0 && ` (${fotosBase64.length} fotos seleccionadas)`}
                                                </Alert>
                                                <FotoUploader
                                                    onFotosChange={setFotosBase64}
                                                    maxFotos={9}
                                                    label="Arrastra o selecciona fotos del incidente"
                                                />
                                            </Grid>

                                            {/* ✅ Sección de Fotos existentes */}
                                            <Grid item xs={12}>
                                                {ticket.materials_used?.fotos && ticket.materials_used.fotos.length > 0 && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="subtitle2" gutterBottom>
                                                            📸 Fotos ya subidas en este incidente
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                            {ticket.materials_used.fotos.map((foto, idx) => (
                                                                <Box
                                                                    key={idx}
                                                                    sx={{
                                                                        width: 80,
                                                                        height: 80,
                                                                        borderRadius: 1,
                                                                        overflow: 'hidden',
                                                                        border: '1px solid #e0e0e0',
                                                                        cursor: 'pointer'
                                                                    }}
                                                                    onClick={() => window.open(foto.url || foto, '_blank')}
                                                                >
                                                                    <img
                                                                        src={foto.url || foto}
                                                                        alt={`Foto ${idx + 1}`}
                                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                    />
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Grid>

                                            {/* ✅ Sección de Materiales Utilizados */}
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <InventoryIcon color="primary" />
                                                    Materiales Utilizados
                                                </Typography>

                                                {ticket.materials_used?.materiales_incidente && ticket.materials_used.materiales_incidente.length > 0 && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Alert severity="info" sx={{ mb: 1 }}>
                                                            <strong>Materiales ya registrados en este incidente:</strong>
                                                        </Alert>
                                                        <TableContainer component={Paper} variant="outlined">
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell><strong>Material</strong></TableCell>
                                                                        <TableCell align="center"><strong>Cantidad</strong></TableCell>
                                                                        <TableCell><strong>Observaciones</strong></TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {ticket.materials_used.materiales_incidente.map((m, idx) => (
                                                                        <TableRow key={idx}>
                                                                            <TableCell>{m.nombre}</TableCell>
                                                                            <TableCell align="center">
                                                                                <Chip label={m.cantidad} size="small" color="primary" />
                                                                            </TableCell>
                                                                            <TableCell>{m.observaciones || '-'}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </Box>
                                                )}

                                                <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Agregar Materiales a Consumir
                                                    </Typography>
                                                    <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={12} md={5}>
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel>Material</InputLabel>
                                                                <Select
                                                                    value={nuevoMaterial.material_id}
                                                                    onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, material_id: e.target.value })}
                                                                    label="Material"
                                                                >
                                                                    <MenuItem value="">Seleccione un material</MenuItem>
                                                                    {catalogoMateriales.map(m => (
                                                                        <MenuItem key={m.id} value={m.id}>
                                                                            {m.nombre} ({m.unidad})
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12} md={2}>
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                label="Cantidad"
                                                                size="small"
                                                                value={nuevoMaterial.cantidad}
                                                                onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, cantidad: parseFloat(e.target.value) || 1 })}
                                                                inputProps={{ min: 1, step: 1 }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <TextField
                                                                fullWidth
                                                                label="Observaciones"
                                                                size="small"
                                                                value={nuevoMaterial.observaciones}
                                                                onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, observaciones: e.target.value })}
                                                                placeholder="Ej: Conector SC/APC"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={1}>
                                                            <Button
                                                                fullWidth
                                                                variant="contained"
                                                                onClick={agregarMaterial}
                                                                startIcon={<AddIcon />}
                                                                disabled={!nuevoMaterial.material_id}
                                                            >
                                                                Agregar
                                                            </Button>
                                                        </Grid>
                                                    </Grid>
                                                </Paper>

                                                {materialesUtilizados.length > 0 && (
                                                    <Box>
                                                        <Alert severity="warning" sx={{ mb: 1 }}>
                                                            <strong>Materiales a consumir al resolver:</strong> ({materialesUtilizados.length} materiales)
                                                        </Alert>
                                                        <List dense sx={{ mb: 2, bgcolor: '#fafafa', borderRadius: 1 }}>
                                                            {materialesUtilizados.map((mat, idx) => (
                                                                <React.Fragment key={idx}>
                                                                    <ListItem>
                                                                        <ListItemText
                                                                            primary={
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    <strong>{mat.nombre}</strong>
                                                                                    <Chip label={`x${mat.cantidad}`} size="small" color="primary" />
                                                                                </Box>
                                                                            }
                                                                            secondary={mat.observaciones || 'Sin observaciones'}
                                                                        />
                                                                        <ListItemSecondaryAction>
                                                                            <IconButton edge="end" onClick={() => eliminarMaterial(idx)} size="small" color="error">
                                                                                <DeleteIconMaterial />
                                                                            </IconButton>
                                                                        </ListItemSecondaryAction>
                                                                    </ListItem>
                                                                    <Divider />
                                                                </React.Fragment>
                                                            ))}
                                                        </List>
                                                    </Box>
                                                )}
                                            </Grid>

                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="URL de evidencia (foto)"
                                                    value={fieldData.evidence_photo_url}
                                                    onChange={(e) => setFieldData({ ...fieldData, evidence_photo_url: e.target.value })}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleUpdateFieldWork}
                                                    disabled={loading}
                                                    startIcon={<EngineeringIcon />}
                                                    sx={{ mr: 2 }}
                                                >
                                                    Guardar Trabajo
                                                </Button>
                                                {canResolve && (
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        onClick={handleResolve}
                                                        disabled={loading}
                                                        startIcon={<CheckCircleIcon />}
                                                    >
                                                        Marcar como Resuelto
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {activeTab === 4 && (
                                    <Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Paper variant="outlined" sx={{ p: 2 }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Notas de resolución
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {ticket.resolution_notes || 'No hay notas de resolución'}
                                                    </Typography>
                                                    {ticket.speed_test_result && (
                                                        <>
                                                            <Divider sx={{ my: 2 }} />
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Test de velocidad
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Download: {ticket.speed_test_result.download} Mbps |
                                                                Upload: {ticket.speed_test_result.upload} Mbps |
                                                                Ping: {ticket.speed_test_result.ping} ms                                                            </Typography>
                                                        </>
                                                    )}

                                                    {ticket.materials_used?.fotos && ticket.materials_used.fotos.length > 0 && (
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                <PhotoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                                Fotos del Incidente
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                {ticket.materials_used.fotos.map((foto, idx) => (
                                                                    <Box
                                                                        key={idx}
                                                                        sx={{
                                                                            width: 80,
                                                                            height: 80,
                                                                            borderRadius: 1,
                                                                            overflow: 'hidden',
                                                                            border: '1px solid #e0e0e0',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                        onClick={() => window.open(foto.url || foto, '_blank')}
                                                                    >
                                                                        <img
                                                                            src={foto.url || foto}
                                                                            alt={`Foto ${idx + 1}`}
                                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                        />
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {ticket.resolved_at && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                                            Resuelto el: {format(new Date(ticket.resolved_at), 'dd/MM/yyyy HH:mm')}
                                                        </Typography>
                                                    )}
                                                    {ticket.closed_at && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                            Cerrado el: {format(new Date(ticket.closed_at), 'dd/MM/yyyy HH:mm')}
                                                        </Typography>
                                                    )}
                                                </Paper>
                                            </Grid>
                                            {canClose && (
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        onClick={handleCloseTicket}
                                                        disabled={loading}
                                                        startIcon={<CheckCircleIcon />}
                                                        fullWidth
                                                    >
                                                        Cerrar Ticket Definitivamente
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    {canCloseInstallation && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleCloseInstallation}
                            disabled={loading}
                            startIcon={<CheckCircleIcon />}
                            sx={{ mr: 1 }}
                        >
                            Completar Instalación
                        </Button>
                    )}
                    {canCloseRetiro && (
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleCloseRetiro}
                            disabled={loading}
                            startIcon={<WarningIcon />}
                            sx={{ mr: 1 }}
                        >
                            Completar Retiro
                        </Button>
                    )}
                    {canCloseCambio && (
                        <Button
                            variant="contained"
                            color="info"
                            onClick={handleCloseCambio}
                            disabled={loading}
                            startIcon={<SwapHorizIcon />}
                            sx={{ mr: 1 }}
                        >
                            Completar Cambio
                        </Button>
                    )}
                    
                    {/* ✅ Botón Reabrir Ticket */}
                    {canReopen && (
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={handleReopenTicket}
                            disabled={loading}
                            startIcon={<ReplayIcon />}
                            sx={{ mr: 1 }}
                        >
                            Reabrir Ticket
                        </Button>
                    )}

                    {user?.is_superuser || user?.roles?.includes('admin') || user?.roles?.includes('supervisor') ? (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDeleteTicket}
                            disabled={loading}
                            startIcon={<DeleteIcon />}
                        >
                            Eliminar Ticket
                        </Button>
                    ) : null}
                    <Button onClick={onClose} disabled={loading}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <CloseInstalacionDialog
                open={closeDialogOpen}
                onClose={() => setCloseDialogOpen(false)}
                ticket={ticket}
                onSuccess={handleCloseSuccess}
            />

            <CloseRetiroDialog
                open={closeRetiroDialogOpen}
                onClose={() => setCloseRetiroDialogOpen(false)}
                ticket={ticket}
                onSuccess={handleCloseSuccess}
            />

            <CloseCambioEquipoDialog
                open={closeCambioDialogOpen}
                onClose={() => setCloseCambioDialogOpen(false)}
                ticket={ticket}
                onSuccess={handleCloseSuccess}
            />

            {/* ✅ Dialog de bitácora del cliente */}
            <ClienteBitacora
                open={bitacoraClienteOpen}
                onClose={() => setBitacoraClienteOpen(false)}
                customerId={ticket?.customer_id}
                customerName={ticket?.customer_name}
            />
        </>
    );
}
